/**
 * How agency roles are grouped in the pickers.
 *
 * Roles have no category of their own, so the sections are kept here by
 * slug. A role in no section lands in "Other", which is the failure worth
 * having: a new role shows up unsorted rather than not at all. Internal
 * roles form their own section from their flag. It comes first: only Roger
 * staff see it, and when they do it's usually the point.
 */

import type { RoleResponse } from '../services/api/types.gen';
import type { PermissionLike } from './permissionAreas';

export type RoleGroup = { title: string; roles: RoleResponse[] };

const SECTIONS: Array<{ title: string; slugs: string[] }> = [
  {
    title: 'Administration',
    slugs: [
      'agency-administrator',
      'admin-external',
      'user-manager',
      'human-resources',
    ],
  },
  {
    title: 'Clinical',
    slugs: [
      'clinical-supervisor',
      'director-of-nursing',
      'emr-field-clinician',
      'case-manager',
      'qa',
      'patient-manager',
      'medical-records',
      'coder',
      'goals:admin',
    ],
  },
  {
    title: 'Intake & scheduling',
    slugs: ['intake', 'scheduling', 'marketing', 'authorizations'],
  },
  {
    title: 'Billing & finance',
    slugs: [
      'billing-and-noa',
      'accounting',
      'payroll-manager',
      'roger-emr-report-viewer',
    ],
  },
  {
    title: 'Scribe',
    slugs: ['rogerdocs:dashboard', 'customizations-tab', 'report-viewer'],
  },
];

/** The permissions a role grants that anybody here should read about. */
export function visiblePermissions(
  role: RoleResponse | undefined
): PermissionLike[] {
  return (role?.permissions ?? []).filter(p => !p.is_internal);
}

/** How much a role grants: its permissions anybody here can read about. */
export function grantCount(role: RoleResponse | undefined): number {
  return visiblePermissions(role).length;
}

/** Orders roles by how much they grant, most first, then by name. */
export function byMostPermissive<T extends { name: string }>(
  grants: (role: T) => number
): (a: T, b: T) => number {
  return (a, b) => grants(b) - grants(a) || a.name.localeCompare(b.name);
}

/** Within a section, the role that grants the most comes first. */
function mostPermissiveFirst(roles: RoleResponse[]): RoleResponse[] {
  return [...roles].sort(byMostPermissive<RoleResponse>(grantCount));
}

/** Sections in display order; empty sections are left out. */
export function groupRoles(roles: readonly RoleResponse[]): RoleGroup[] {
  const bySlug = new Map(roles.map(role => [role.slug, role]));
  const placed = new Set<string>();
  const groups: RoleGroup[] = [];
  const push = (title: string, members: RoleResponse[]) => {
    if (members.length) {
      groups.push({ title, roles: mostPermissiveFirst(members) });
    }
  };
  // First, since only Roger staff see it and it's what they came for.
  push(
    'Roger internal',
    roles.filter(role => role.is_internal)
  );
  for (const section of SECTIONS) {
    const members = section.slugs
      .map(slug => bySlug.get(slug))
      .filter((role): role is RoleResponse => !!role && !role.is_internal);
    for (const role of members) placed.add(role.slug);
    push(section.title, members);
  }
  push(
    'Other',
    roles.filter(role => !placed.has(role.slug) && !role.is_internal)
  );
  return groups;
}
